import { Component, computed, inject, input, output, signal, TemplateRef, viewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  AbilityService,
  ApiCallingService,
  NgbModalService,
  SignaturePadDialogComponent,
  SpinnerService
} from '@zayan-erp/shared';
import { DealDocumentSignature } from 'libs/sales/src/lib/models/deal-document.models';
import { NgxExtendedPdfViewerModule, NgxExtendedPdfViewerService } from 'ngx-extended-pdf-viewer';
import { SalesApsService } from 'libs/sales/src/lib/services/sales-aps.service';
import { switchMap } from 'rxjs';
import { RecipientStatus } from 'libs/sales/src/lib/models/deal-document.models';
import { environment } from 'apps/cinari-erp-client/src/environments/environment';

@Component({
  selector: 'in-person-signature-view',
  imports: [NgxExtendedPdfViewerModule, SignaturePadDialogComponent],
  templateUrl: './in-person-signature-view.component.html'
})
export class InPersonSignatureViewComponent {
  readonly RecipientStatus = RecipientStatus;
  readonly #representativeSignName = environment.projectExecutiveNameForDocumentSigning;
  modalService = inject(NgbModal);
  ngbModalService = inject(NgbModalService);
  pdfService = inject(NgxExtendedPdfViewerService);
  salesApsService = inject(SalesApsService);
  apiCallingService = inject(ApiCallingService);
  spinnerService = inject(SpinnerService);
  #abilityService = inject(AbilityService);

  readonly canWriteDeals = computed(() => this.#abilityService.ability.can('write-sales-deals', 'deals'));

  signaturePadModal = viewChild<TemplateRef<SignaturePadDialogComponent>>('signaturePadModal');

  signers = input.required<DealDocumentSignature[]>();
  documentId = input.required<string>();
  documentUrl = input.required<string>();
  focusedSigner = input<string>(); // might use in future
  onDocumentSigned = output<string>();

  savedSignature: string | null = null;
  isPdfLoaded = signal<boolean>(false);

  displaySignerName(signer: DealDocumentSignature): string {
    if (signer.entityType?.toLowerCase?.() === 'user') {
      return this.#representativeSignName;
    }

    return signer.signee.name ?? 'Unknown';
  }

  displaySignerType(signer: DealDocumentSignature): string {
    return this.salesApsService.formatSigneeType(signer.entityType);
  }

  onPdfLoadingStarts(): void {
    this.isPdfLoaded.set(false);
    this.spinnerService.handleSpinnerStart();
  }

  onPdfLoaded(): void {
    this.isPdfLoaded.set(true);
    this.spinnerService.handleSpinnerStop();
  }

  onPdfLoadingFailed(error: Error): void {
    this.isPdfLoaded.set(false);
    this.spinnerService.handleSpinnerStop();
  }

  openSignaturePad(): void {
    if (!this.canWriteDeals()) return;
    this.savedSignature = null;
    const modal = this.modalService.open(this.signaturePadModal(), {
      ...this.ngbModalService.centerModal,
      size: 'lg'
    });

    modal.dismissed.subscribe(() => {
      this.addSignatureToPdf();
      // this.addImageToAnnotationLayer();
    });
  }

  async addSignatureToPdf(): Promise<void> {
    if (!this.savedSignature) {
      alert('No signature found');
      return;
    }
    await this.pdfService.addImageToAnnotationLayer({
      urlOrDataUrl: this.savedSignature,
      page: 0,
      top: '50%',
      right: '20%',
      bottom: '40%',
      left: '20%'
    });
  }

  /**
   * Checks if any signature/annotation has been added to the document in the current session.
   * This only returns annotations added via the editor layer, not pre-existing PDF annotations.
   */
  async hasSignatureBeenAdded(): Promise<boolean> {
    const annotations = await this.pdfService.getSerializedAnnotations();
    return annotations !== null && (annotations?.length ?? 0) > 0;
  }

  async setSignatureForSigner(signerId: string): Promise<void> {
    if (!this.canWriteDeals()) return;
    // Validate that a signature has been added before proceeding
    const hasSignature = await this.hasSignatureBeenAdded();
    if (!hasSignature) {
      alert('Please add your signature to the document before submitting.');
      return;
    }

    const blob = await this.pdfService.getCurrentDocumentAsBlob();
    if (!blob) {
      alert('Failed to get the document. Please try again.');
      return;
    }

    // Creating a new PDF file with the received blob
    const pdfFile = new File([blob], `${this.documentId()}.pdf`, { type: 'application/pdf' });
    const documentId = this.documentId();

    // Chain of API calls:
    // 1. Get presigned URL for upload
    // 2. Upload signed PDF to S3
    // 3. Update signer status with new document ID
    this.salesApsService
      .uploadSignedPdfDocument(pdfFile)
      .pipe(
        switchMap((uploadResponse: { presignedUrl: string; s3Key: string; documentId: string }) =>
          this.apiCallingService
            .awsFileUpload(uploadResponse.presignedUrl, pdfFile, true)
            .pipe(
              switchMap(() =>
                this.salesApsService.updateDocumentSignerStatus(documentId, signerId, uploadResponse.documentId)
              )
            )
        )
      )
      .subscribe({
        next: () => {
          this.onDocumentSigned.emit(signerId);
        },
        error: (error: Error) => {
          console.error('Failed to complete signing process:', error);
          alert('Failed to save signed document. Please try again.');
        }
      });
  }
}
